/**
 * usePageTracking.js
 * Hook para tracking de navegación de páginas
 * Envía eventos de navegación al backend para el log de actividad
 *
 * v3.9.1: Enhanced to send tab, subtab, billerId, publisherId context
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../services/api';

// Mapeo de rutas a nombres de módulo
const routeModuleMap = {
  '/platform/dashboard': 'Dashboard Plataforma',
  '/publisher-systems': 'Canales',
  '/billers': 'Empresas',
  '/users': 'Usuarios',
  '/roles': 'Roles',
  '/oauth-admin': 'OAuth Admin',
  '/packages': 'Paquetes',
  '/ia-exchange': 'IA Exchange',
  '/prices': 'Precios',
  '/industries': 'Industrias',
  '/monitoring': 'Monitoreo',
  '/channels': 'Canales de Pago',
  '/logs': 'Logs',
  '/assets/templates': 'Templates',
  '/assets/series': 'Series de Datos',
  '/assets/logos': 'Logos',
  '/assets/onboarding': 'Onboarding',
  '/assets/help-tooltips': 'Ayudas',
  '/notification-vectors': 'Vectores de Notificación',
  '/admin/tenants': 'Tenants',
  '/admin/connections': 'Conexiones',
  '/admin/schedulers': 'Schedulers',
  '/admin/scheduler-groups': 'Grupos Schedulers',
  '/admin/import': 'Import',
  '/admin/ai-config': 'Configuración IA',
  '/docs': 'Documentación',
  '/apidoc': 'API Doc',
  '/profile': 'Perfil',
};

// Patrones dinámicos con extracción de IDs
const dynamicRoutePatterns = [
  { pattern: /^\/publisher-dashboard\/(\d+)/, module: 'Dashboard Publisher', extractPublisherId: 1 },
  { pattern: /^\/billers\/([^/]+)\/dashboard/, module: 'Dashboard Biller', extractBillerId: 1 },
  { pattern: /^\/billers\/([^/]+)/, module: 'Detalle Empresa', extractBillerId: 1 },
];

function getModuleInfo(pathname, searchParams) {
  let moduleName = null;
  let billerId = null;
  let publisherSystemId = null;

  // Check exact matches first
  if (routeModuleMap[pathname]) {
    moduleName = routeModuleMap[pathname];
  } else {
    // Check dynamic patterns
    for (const { pattern, module, extractBillerId, extractPublisherId } of dynamicRoutePatterns) {
      const match = pathname.match(pattern);
      if (match) {
        moduleName = module;
        if (extractBillerId) billerId = match[extractBillerId];
        if (extractPublisherId) publisherSystemId = match[extractPublisherId];
        break;
      }
    }
  }

  if (!moduleName) return null;

  // Extract tab/subtab from search params
  const tab = searchParams.get('tab') || null;
  const subtab = searchParams.get('subtab') || null;

  return { moduleName, billerId, publisherSystemId, tab, subtab };
}

export default function usePageTracking() {
  const location = useLocation();
  const lastTrackedKey = useRef(null);
  const debounceTimer = useRef(null);

  useEffect(() => {
    const pathname = location.pathname;
    const searchParams = new URLSearchParams(location.search);

    const info = getModuleInfo(pathname, searchParams);
    if (!info) return;

    // Build tracking key: path + tab + subtab to detect tab changes
    const trackingKey = `${pathname}|${info.tab || ''}|${info.subtab || ''}`;

    // Skip if same tracking key (prevents double tracking)
    if (trackingKey === lastTrackedKey.current) return;

    // Debounce: wait 500ms before sending to avoid rapid navigation tracking
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      lastTrackedKey.current = trackingKey;

      const payload = {
        module: info.moduleName,
        path: pathname,
      };

      // Add context if available
      if (info.billerId) payload.biller_id = info.billerId;
      if (info.publisherSystemId) payload.publisher_system_id = info.publisherSystemId;
      if (info.tab) payload.tab = info.tab;
      if (info.subtab) payload.subtab = info.subtab;

      api.post('/auth/track-navigation', payload).catch(() => {
        // Silently fail - tracking should never affect UX
      });
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [location.pathname, location.search]);
}

/**
 * Track a tab/subtab change from within a component (e.g., BillerDashboard)
 * This is for tab changes that don't update the URL
 */
export function trackTabChange(module, tab, subtab, billerId) {
  const payload = {
    module,
    path: window.location.pathname,
    tab,
  };
  if (subtab) payload.subtab = subtab;
  if (billerId) payload.biller_id = billerId;

  api.post('/auth/track-navigation', payload).catch(() => {
    // Silently fail
  });
}
